import { useMemo, useState } from 'react';
import { X, Search, ArrowRight, Loader2 } from 'lucide-react';
import { StandardIngredient } from '../../types/ingredients';
import { mergeIngredientApi } from '../../utils/ingredientsModerationApi';

interface IngredientMergeModalProps {
  source: StandardIngredient;
  // Catalogue complet (tous statuts) — on filtre les pending et le source lui-même.
  catalog: StandardIngredient[];
  onClose: () => void;
  onSuccess: (info: { mergedInto: string; recipesUpdated: number }) => void;
}

function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

export function IngredientMergeModal({
  source,
  catalog,
  onClose,
  onSuccess,
}: IngredientMergeModalProps) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<StandardIngredient | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const candidates = useMemo(() => {
    const normalizedQuery = normalize(query);
    return catalog
      .filter((ing) => {
        if (ing.id === source.id) return false;
        // Seuls les ingrédients validés (ou legacy sans status) sont des cibles valides.
        if (ing.status === 'pending') return false;
        if (!normalizedQuery) return true;
        const inName = normalize(ing.name).includes(normalizedQuery);
        const inAliases = (ing.aliases || []).some((a) => normalize(a).includes(normalizedQuery));
        return inName || inAliases;
      })
      .slice(0, 25);
  }, [catalog, source.id, query]);

  const handleConfirm = async () => {
    if (!selected) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await mergeIngredientApi(source.id, selected.id);
      onSuccess({ mergedInto: selected.id, recipesUpdated: result.recipesUpdated });
    } catch (err: any) {
      console.error('Merge failed:', err);
      setError(err?.message || 'La fusion a échoué.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-menioo-navy">Fusionner l'ingrédient</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              «&nbsp;<span className="font-medium">{source.name}</span>&nbsp;» sera ajouté
              comme alias et les recettes basculées sur la cible choisie.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="p-1 rounded hover:bg-gray-100 disabled:opacity-50"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher l'ingrédient cible…"
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-menioo-green focus:border-transparent"
            />
          </div>

          <div className="border border-gray-200 rounded-md max-h-80 overflow-y-auto divide-y divide-gray-100">
            {candidates.length === 0 ? (
              <p className="p-4 text-sm text-gray-500 text-center">Aucun ingrédient trouvé.</p>
            ) : (
              candidates.map((ing) => {
                const isSelected = selected?.id === ing.id;
                return (
                  <button
                    key={ing.id}
                    type="button"
                    onClick={() => setSelected(ing)}
                    className={`w-full px-3 py-2 flex items-center justify-between text-left text-sm hover:bg-gray-50 ${
                      isSelected ? 'bg-menioo-green-light' : ''
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900 truncate">{ing.name}</div>
                      <div className="text-xs text-gray-500 truncate">
                        {ing.category} · {ing.language} ·{' '}
                        {(ing.aliases || []).slice(0, 4).join(', ') || 'aucun alias'}
                      </div>
                    </div>
                    {isSelected && (
                      <span className="text-xs font-medium text-menioo-green shrink-0 ml-2">
                        Sélectionné
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>

          {selected && (
            <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-sm text-blue-900">
              <div className="flex items-center gap-2">
                <span className="font-medium">{source.name}</span>
                <ArrowRight className="h-4 w-4" />
                <span className="font-medium">{selected.name}</span>
              </div>
              <p className="text-xs text-blue-700 mt-1">
                «&nbsp;{source.name}&nbsp;» sera ajouté en alias de «&nbsp;{selected.name}&nbsp;».
                Toutes les recettes pointant vers «&nbsp;{source.name}&nbsp;» seront migrées.
              </p>
            </div>
          )}

          {error && (
            <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!selected || submitting}
            className="px-4 py-2 text-sm font-medium text-white bg-menioo-green rounded-md hover:bg-menioo-green-dark disabled:opacity-50 flex items-center gap-2"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {submitting ? 'Fusion en cours…' : 'Fusionner'}
          </button>
        </div>
      </div>
    </div>
  );
}
